import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { updateProfile, changePassword, getSavedTokens, deleteSavedToken } from '../services/api';
import LlmKeysPanel from '../components/LlmKeysPanel';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  User, Lock, Shield, ShieldCheck, Eye, Mail,
  Loader2, Save, KeyRound, CheckCircle2, Database, Trash2
} from 'lucide-react';

const roleBadge = {
  admin: { label: 'Administrator', color: 'bg-red-100 text-red-700 border-red-200', icon: ShieldCheck, desc: 'Full access — manage users, approve deletions, all CRUD operations' },
  editor: { label: 'Editor', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Shield, desc: 'Create, edit, upload — cannot delete or manage users' },
  reader: { label: 'Reader', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Eye, desc: 'View-only access to documentation and projects' },
};

const SettingsPage = () => {
  const { user, role, login } = useAuth();

  // Profile form
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);


  // Saved tokens
  const [savedTokens, setSavedTokens] = useState([]);

  const fetchSavedTokenList = async () => {
    setTokensLoading(true);
    try {
      const res = await getSavedTokens();
      setSavedTokens(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load saved tokens');
    } finally {
      setTokensLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedTokenList();
  }, []);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [deletingTokenId, setDeletingTokenId] = useState(null);
  const badge = roleBadge[role] || roleBadge.reader;
  const BadgeIcon = badge.icon;

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    setProfileLoading(true);
    setProfileSaved(false);
    try {
      const updates = {};
      if (name.trim() !== user?.name) updates.name = name.trim();
      if (email.trim() !== user?.email) updates.email = email.trim();
      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setProfileLoading(false);
        return;
      }
      await updateProfile(updates);
      setProfileSaved(true);
      toast.success('Profile updated successfully');
      // Refresh user data — re-login silently using the same token
      // The AuthContext will refetch on next mount
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleDeleteSavedToken = async (tokenId) => {
    setDeletingTokenId(tokenId);
    try {
      await deleteSavedToken(tokenId);
      setSavedTokens((prev) => prev.filter((t) => t.id !== tokenId));
      toast.success('Saved token deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete saved token');
    } finally {
      setDeletingTokenId(null);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!currentPassword) { toast.error('Current password is required'); return; }
    if (newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setPasswordLoading(true);
    setPasswordChanged(false);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      setPasswordChanged(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="settings-title">
            Settings
          </h1>
          <p className="text-slate-500 mt-1">Manage your profile and security preferences</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="h-11" data-testid="settings-tabs">
            <TabsTrigger value="profile" className="flex items-center gap-2" data-testid="tab-profile">
              <User className="h-4 w-4" /> Profile
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2" data-testid="tab-security">
              <Lock className="h-4 w-4" /> Security
            </TabsTrigger>
            <TabsTrigger value="tokens" className="flex items-center gap-2" data-testid="tab-saved-tokens">
              <Database className="h-4 w-4" /> Saved Tokens
            </TabsTrigger>
            {role === 'admin' && (
              <TabsTrigger value="api-keys" className="flex items-center gap-2" data-testid="tab-api-keys">
                <KeyRound className="h-4 w-4" /> API Keys
              </TabsTrigger>
            )}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            {/* Role Badge */}
            <Card className="border-slate-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#2B8FD8] flex items-center justify-center text-white text-xl font-bold shrink-0">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-slate-900">{user?.name}</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${badge.color}`}>
                        <BadgeIcon className="h-3 w-3" />
                        {badge.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{badge.desc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Edit Profile */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Plus_Jakarta_Sans']">Edit Profile</CardTitle>
                <CardDescription>Update your display name and email address</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium">Display Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@company.com"
                        className="pl-10"
                        data-testid="profile-email-input"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <Button type="submit" disabled={profileLoading} className="bg-[#2B8FD8] hover:bg-[#2075b3]" data-testid="save-profile-btn">
                      {profileLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Changes
                    </Button>
                    {profileSaved && (
                      <span className="flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle2 className="h-4 w-4" /> Saved
                      </span>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Account Info */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Plus_Jakarta_Sans']">Account Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">User ID</span>
                    <span className="text-slate-700 font-mono text-xs">{user?.id}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Member since</span>
                    <span className="text-slate-700">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-slate-500">Role</span>
                    <Badge variant="outline" className="text-xs">{role}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-slate-500" />
                  Change Password
                </CardTitle>
                <CardDescription>Ensure your account stays secure by using a strong password</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current-password" className="text-sm font-medium">Current Password</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-sm font-medium">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 chars)"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-sm font-medium">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      data-testid="confirm-password-input"
                    />
                    {newPassword && confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-xs text-red-500">Passwords do not match</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <Button type="submit" disabled={passwordLoading} className="bg-[#2B8FD8] hover:bg-[#2075b3]" data-testid="change-password-btn">
                      {passwordLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                      Change Password
                    </Button>
                    {passwordChanged && (
                      <span className="flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle2 className="h-4 w-4" /> Password changed
                      </span>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-amber-50/50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Password Security Tips</p>
                    <ul className="text-xs text-amber-700 mt-2 space-y-1 list-disc ml-4">
                      <li>Use at least 8 characters with mixed case, numbers, and symbols</li>
                      <li>Don't reuse passwords from other services</li>
                      <li>If you suspect unauthorized access, change your password immediately</li>
                      <li>Contact an admin if you're locked out — they can reset your password</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Saved Tokens Tab */}
          <TabsContent value="tokens" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                  <Database className="h-5 w-5 text-slate-500" />
                  Saved Repository Tokens
                </CardTitle>
                <CardDescription>
                  Tokens are encrypted at rest and scoped to your account. They are auto-used when URL/provider/account match and token field is left blank.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {tokensLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="saved-tokens-loading">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading saved tokens...
                  </div>
                ) : savedTokens.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="saved-tokens-empty">
                    No saved tokens yet. Import from GitHub/Azure/Bitbucket with a token once, and it will be saved automatically.
                  </p>
                ) : (
                  <div className="space-y-3" data-testid="saved-tokens-list">
                    {savedTokens.map((tokenRow) => (
                      <div key={tokenRow.id} className="border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">{tokenRow.provider}</Badge>
                            <span className="text-sm font-medium text-slate-800">{tokenRow.host}</span>
                            <span className="text-xs text-slate-400">/</span>
                            <span className="text-sm text-slate-700">{tokenRow.account}</span>
                          </div>
                          <p className="text-xs text-slate-500">
                            Updated: {tokenRow.updated_at ? new Date(tokenRow.updated_at).toLocaleString() : '—'}
                            {' · '}
                            Last used: {tokenRow.last_used_at ? new Date(tokenRow.last_used_at).toLocaleString() : '—'}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleDeleteSavedToken(tokenRow.id)}
                          disabled={deletingTokenId === tokenRow.id}
                          data-testid={`delete-saved-token-${tokenRow.id}`}
                        >
                          {deletingTokenId === tokenRow.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4 mr-1" /> Delete
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Keys Tab (admin only) */}
          {role === 'admin' && (
            <TabsContent value="api-keys" className="space-y-6">
              <LlmKeysPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};

export default SettingsPage;
